import CourseCard from 'components/CourseDashboard/CourseCard';
import NoCourses from 'components/CourseDashboard/NoCourses';
import { useTranslation } from 'react-i18next';
import {
    Button,
    ButtonGroup,
    ButtonGroupSelectedVariant,
    Container,
    Icon,
    IconButton,
    Loading,
    Stack,
    styled,
    Tabs,
    Text,
    TextInput,
} from 'shell/components';
import { CourseLobbyLayout } from 'utils/constants';

const Mobile = ({
    courses,
    filteredCourses,
    handleImportCourse,
    searchBarInput,
    has_instructor,
    onChangeSearchBarInput,
    filterKey,
    setIsFilterDrawerOpen,
    selectCourse,
    handleCourseCardDropdown,
    viewArchived,
    setViewArchived,
    courseLobbyLayout,
    setCourseLobbyLayout,
    hasCoursesInView,
}) => {
    const { t } = useTranslation('course');
    const gridLayoutLabel = t('common:controls.layout.grid', 'Grid');
    const listLayoutLabel = t('common:controls.layout.list', 'List');

    if (!courses) return <Loading />;

    return (
        <Container device={'mobile'}>
            <Stack sx={{ width: '100%' }} spacing={4}>
                <Stack spacing={4}>
                    <HeaderStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Text component={'h1'} variant={'h6'} weight={'medium'}>
                            {t('course-lobby.title', 'Course Lobby')}
                        </Text>
                        {has_instructor && (
                            <IconButton
                                aria-label={'create course'}
                                className={'joyride-step-1'}
                                kind={'add'}
                                size={'small'}
                                variant={'circle'}
                                onClick={handleImportCourse}
                            />
                        )}
                    </HeaderStack>
                    <HeaderStack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={2.5}>
                        <Stack direction={'row'} spacing={2.5}>
                            <Button
                                color={'neutral'}
                                startIcon={<Icon kind={'filter'} />}
                                variant={'secondary'}
                                onClick={() => setIsFilterDrawerOpen(true)}
                            ></Button>
                            <LayoutButtonGroup
                                aria-label={'course lobby layout'}
                                iconOnly
                                options={[
                                    {
                                        key: 'grid',
                                        value: gridLayoutLabel,
                                        icon: 'taskBoard',
                                        tooltip: gridLayoutLabel,
                                    },
                                    {
                                        key: 'list',
                                        value: listLayoutLabel,
                                        icon: 'listUnordered',
                                        tooltip: listLayoutLabel,
                                    },
                                ]}
                                selected={courseLobbyLayout}
                                selectedVariant={ButtonGroupSelectedVariant.SUBTLE}
                                onChange={(layout) => {
                                    setCourseLobbyLayout(layout);
                                }}
                            />
                        </Stack>
                        <CustomTextInput
                            disablePointerEventsOnIcon={!Boolean(searchBarInput)}
                            endIcon={
                                searchBarInput ? (
                                    <IconButton
                                        aria-label={'clear search input'}
                                        kind={'exit'}
                                        variant={'square'}
                                        onClick={() => onChangeSearchBarInput('')}
                                        onMouseDown={(e) => e.preventDefault()}
                                    />
                                ) : (
                                    <Icon kind={'search'} />
                                )
                            }
                            placeholder={t('common:controls.search', 'Search')}
                            value={searchBarInput}
                            onChange={(e) => onChangeSearchBarInput(e.target.value)}
                        />
                    </HeaderStack>
                    <Tabs
                        aria-label={'course lobby tabs'}
                        options={[
                            { key: 'current', value: t('course-lobby.current-courses', 'Current Courses') },
                            { key: 'archived', value: t('course-lobby.archived-courses', 'Archived Courses') },
                        ]}
                        selected={viewArchived ? 'archived' : 'current'}
                        onChange={(key) => setViewArchived(key === 'archived')}
                    />
                </Stack>
                {!hasCoursesInView && <NoCourses type={viewArchived ? 'archived' : 'active'} />}
                {!!filteredCourses.length && (
                    <CoursesStack
                        alignItems={'flex-start'}
                        direction={courseLobbyLayout === CourseLobbyLayout.GRID ? 'row' : 'column'}
                        justifyContent={'flex-start'}
                        layout={courseLobbyLayout}
                        spacing={3}
                    >
                        {filteredCourses.map((course, index) => {
                            return (
                                <CourseCard
                                    key={course.course_id}
                                    className={!index ? 'joyride-step-2' : undefined}
                                    course={course}
                                    handleCourseCardDropdown={handleCourseCardDropdown}
                                    layout={courseLobbyLayout}
                                    onClick={selectCourse}
                                />
                            );
                        })}
                    </CoursesStack>
                )}
            </Stack>
        </Container>
    );
};

const HeaderStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CoursesStack = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'layout',
})(({ theme, layout }) => {
    return {
        flexWrap: layout === CourseLobbyLayout.GRID ? 'wrap' : 'nowrap',
        marginBottom: theme.spacing(8),
        width: '100%',
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        width: '13.5rem',
    };
});

const LayoutButtonGroup = styled(ButtonGroup)(({ theme }) => {
    return {
        height: theme.spacing(9),
        '& button.MuiButton-root': {
            maxHeight: '100%',
            minHeight: theme.spacing(7),
        },
    };
});

export default Mobile;
